import { differenceInCalendarDays } from 'date-fns';
import { type ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { Typography } from '@/components/ui/typography';
import { formatShortDate } from '@/utils/date-util';
import {
  formatInTimeZone,
  getRangeTimeZoneLabel,
  localDayAt,
} from '@/utils/time-zone';

import { DateRangePanel } from '../date-range-panel';
import { TimeZoneChip, TimeZoneChipZone } from '../time-zone-chip';

interface HiringGoalDateRangeProps {
  startDate: string;
  endDate?: string;
  daysElapsed: number;
  addEndDateUrl: string;
  /** The opening's IANA zone; when set, start and end show their times there. */
  timeZone?: string;
  /** The instant the goal's window ends, resolved by the API. */
  endAt?: string | null;
}

// The goal's start and end at the opening, the zone label for the days they span,
// and how many of those days the goal runs.
function useGoalWindow(
  startDate: string,
  endAt: string | null | undefined,
  timeZone: string | undefined,
) {
  return useMemo(() => {
    if (!timeZone) return null;

    const startAt = new Date(startDate);
    const start = formatInTimeZone(startAt, timeZone);
    const startDay = localDayAt(startAt, timeZone);
    if (!start || !startDay) return null;

    const endInstant = endAt ? new Date(endAt) : null;
    const end = endInstant ? formatInTimeZone(endInstant, timeZone) : null;
    const endDay = endInstant ? localDayAt(endInstant, timeZone) : null;
    // An open-ended goal has no last day, so no one abbreviation holds for all of it.
    const timeZoneLabel = endDay
      ? getRangeTimeZoneLabel(startDay, endDay, timeZone)
      : timeZone;
    if (!timeZoneLabel) return null;

    return {
      start,
      end,
      timeZoneLabel,
      totalDays: endDay ? differenceInCalendarDays(endDay, startDay) + 1 : null,
    };
  }, [startDate, endAt, timeZone]);
}

function GoalStamp({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Typography variant="caption">{label}</Typography>
      <Typography variant="body2" className="whitespace-nowrap">
        {children}
      </Typography>
    </div>
  );
}

function GoalInstant({ date, time }: { date: string; time: string }) {
  return (
    <>
      {date} <span className="text-fx-neutral-600 font-normal">{time}</span>
    </>
  );
}

export function HiringGoalDateRange({
  startDate,
  endDate,
  daysElapsed,
  addEndDateUrl,
  timeZone,
  endAt,
}: HiringGoalDateRangeProps) {
  const goalWindow = useGoalWindow(startDate, endAt, timeZone);

  const addEndDateLink = (
    <Link
      to={addEndDateUrl}
      target="_blank"
      className="text-fx-primary underline underline-offset-4 hover:underline"
    >
      <FormattedMessage
        defaultMessage="+ Add end date"
        description="Link to add an end date to the hiring goal"
      />
    </Link>
  );

  const elapsed = (
    <span data-testid="hiring-goal-elapsed">
      {goalWindow?.totalDays != null ? (
        <FormattedMessage
          defaultMessage="Elapsed: {count} / {total, plural, one {# day} other {# days}}"
          description="Elapsed days out of the hiring goal's length, both counted on the opening's calendar; {count} days elapsed, {total} days in the goal"
          values={{ count: daysElapsed, total: goalWindow.totalDays }}
        />
      ) : (
        <FormattedMessage
          defaultMessage="Elapsed: {count, plural, one {# day} other {# days}}"
          description="Elapsed days in the hiring goal; {count} is the number of days"
          values={{ count: daysElapsed }}
        />
      )}
    </span>
  );

  if (!timeZone || !goalWindow) {
    return (
      <DateRangePanel subtitle={elapsed}>
        <Typography
          variant="body2"
          className="text-fx-neutral-900 font-medium whitespace-nowrap"
        >
          {formatShortDate(startDate)}
          {' – '}
          {endDate ? formatShortDate(endDate) : addEndDateLink}
        </Typography>
      </DateRangePanel>
    );
  }

  return (
    <DateRangePanel
      hideCaption
      subtitle={elapsed}
      timeZoneChip={
        <TimeZoneChip timeZone={timeZone}>
          <FormattedMessage
            defaultMessage="All times in <zone>{timeZone}</zone>"
            description="Chip below the hiring goal's dates. {timeZone} is an IANA zone with its abbreviation, such as America/Chicago (CDT)"
            values={{
              timeZone: goalWindow.timeZoneLabel,
              zone: chunks => <TimeZoneChipZone>{chunks}</TimeZoneChipZone>,
            }}
          />
        </TimeZoneChip>
      }
    >
      <div className="flex gap-6" data-testid="hiring-goal-window">
        <GoalStamp
          label={
            <FormattedMessage
              defaultMessage="Starts"
              description="Caption above the hiring goal's start date and time"
            />
          }
        >
          <GoalInstant {...goalWindow.start} />
        </GoalStamp>
        <GoalStamp
          label={
            <FormattedMessage
              defaultMessage="Ends"
              description="Caption above the hiring goal's end date and time"
            />
          }
        >
          {goalWindow.end ? (
            <GoalInstant {...goalWindow.end} />
          ) : (
            addEndDateLink
          )}
        </GoalStamp>
      </div>
    </DateRangePanel>
  );
}
